/** Datetime component renders a datetime string in the provided timezone, or in the browser's timezone if SHIFT key is pressed and hold. */
import cx from 'clsx';
import { dateRangeToTimezonedYMHM, dateToTimezonedFormated } from 'common/date.ts';
import { getRuntimeTimezone } from 'common/helpers.ts';
import React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../hook/useDateFormat.ts';

type Props = {
  className?: string;
  layout?: 'x' | 'y';
  timezone: string;
  truncateRegion?: boolean;
  rangeFrom?: string | null;
  children: string | null;
};

export const Datetime: React.FC<Props> = ({ className, layout = 'x', timezone, truncateRegion = false, rangeFrom, children: datetime, ...otherProps }: Props) => {
  const [forceBrowserTimezone, setForceBrowserTimezone] = React.useState(false);
  const { currentDateFormat } = useDateFormat();
  const { t } = useTranslation();

  useHotkeys('SHIFT', () => setForceBrowserTimezone(true), { keydown: true });
  useHotkeys('SHIFT', () => setForceBrowserTimezone(false), { keyup: true });

  const timezoneToUse = forceBrowserTimezone ? getRuntimeTimezone() : timezone;
  const formattedDatetime = datetime ? dateToTimezonedFormated(datetime, timezoneToUse, undefined, currentDateFormat) : null;
  const formattedDatetimeRange = rangeFrom ? dateRangeToTimezonedYMHM(rangeFrom, datetime, timezoneToUse, currentDateFormat) : null;
  const formattedTimezone = truncateRegion ? timezoneToUse.split('/')[1] : timezoneToUse;

  return (
    <span className={cx('flex w-max gap-1', layout === 'x' ? 'items-baseline' : 'flex-col items-center', className)} {...otherProps}>
      {formattedDatetime && (
        <React.Fragment>
          {rangeFrom ? formattedDatetimeRange : formattedDatetime}
          <span className="whitespace-nowrap text-sm">({t('labels.timezoneTime', { timezone: formattedTimezone })})</span>
        </React.Fragment>
      )}
      {!formattedDatetime && '-'}
    </span>
  );
};
